<template>
  <div class="header-wrapper">
    <Banner />

    <div class="header" :class="{ 'header--mega-nav-open': isMegaNavOpen }">
      <div class="header__left">
        <a href="/">
          <img src="@/assets/images/logo.svg" class="header__logo" />
        </a>
      </div>

      <div class="header__middle">
        <ul class="nav">
          <li
            v-for="item in navigation.items"
            :key="item.slug"
            class="nav__item"
          >
            <Button
              form="underline-invert"
              :class="getLinkClass(item)"
              :href="item.url"
              tag="a"
              @mouseover="openMegaNav(item.slug)"
              @mouseleave="delayedCloseMegaNav"
            >
              {{ item.title }}
            </Button>
          </li>
        </ul>
      </div>

      <div class="header__right">
        <IconButton
          form="transparent"
          :tag="mq.mdPlus ? 'button' : 'a'"
          class="header__icon"
          data-testid="search"
          icon="search"
          :href="mq.mdPlus ? '' : '/search'"
          @click="mq.mdPlus ? setSearchOpen(true) : null"
        />
        <IconButton
          form="transparent"
          :tag="isLoggedIn ? 'a' : 'button'"
          class="header__icon"
          icon="account"
          :href="isLoggedIn ? accountUrl : ''"
          @click="!isLoggedIn && setAccountOpen(true)"
        />
        <IconButton
          form="transparent"
          class="header__icon"
          icon="bag"
          :notification-number="lineItemsCount"
          @click="setCartOpen(true)"
        />
        <IconButton
          v-if="!mq.xlPlus"
          form="transparent"
          class="header__icon"
          data-testid="burger-menu"
          icon="burger"
          @click="toggleMobileMenu()"
        />
      </div>
    </div>

    <DesktopMegaNav />
  </div>

  <Transition name="slide-in-right">
    <BurgerMenu v-if="isBurgerMenuOpen" />
  </Transition>
  <BagV2 />
  <Search v-if="mq.mdPlus" />
  <LoginPanel />
  <DesktopNavigation v-if="mq.mdPlus" />
</template>

<script>
import IconButton from "@/components/IconButton.vue";
import Banner from "@/components/Banner.vue";
import Button from "@/components/ButtonV2.vue";
import BagV2 from "@/components/BagV2.vue";
import Search from "@/components/SearchDesktop.vue";
import LoginPanel from "@/components/LoginPanel.vue";
import DesktopNavigation from "@/components/navigation/DesktopNavigation.vue";
import DesktopMegaNav from "@/components/navigation/DesktopMegaNav.vue";
import BurgerMenu from "@/components/BurgerMenu.vue";
import { mapActions, mapState, mapGetters } from "vuex";
// import navigationContent from "@/content/navigation.json";

export default {
  name: "HeaderComponent",

  components: {
    IconButton,
    BagV2,
    BurgerMenu,
    LoginPanel,
    Banner,
    DesktopNavigation,
    Search,
    Button,
    DesktopMegaNav,
  },
  inject: ["mq"],
  props: {
    version: {
      type: String,
      default: "2",
    },
  },
  computed: {
    ...mapState("app", ["isBurgerMenuOpen", "isMegaNavOpen"]),
    ...mapState("auth", ["multipassAccount"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("cart", ["lineItemsCount"]),
    accountUrl() {
      return this.multipassAccount.url || "/account/login";
    },
    isTemplateIndex() {
      return document
        .querySelector("#app")
        .classList.contains("template-index");
    },
    navigation() {
      try {
        return require(`@/content/navigationV${this.version}.json`);
      } catch (e) {
        console.error(e);
        return require(`@/content/navigationV2.json`);
      }
    },
  },
  mounted() {
    this.setNavVersion(this.version);
  },
  methods: {
    ...mapActions("app", [
      "toggleMobileMenu",
      "setAccountOpen",
      "setNavOpen",
      "openMegaNav",
      "delayedCloseMegaNav",
      "setNavVersion",
    ]),
    ...mapActions("search", ["setSearchOpen"]),
    ...mapActions("cart", ["setCartOpen"]),
    hasChildLinks(item) {
      if (item.mega?.columns.length > 0) {
        return true;
      }
      return false;
    },
    getLinkClass(item) {
      const baseClass = "nav__link";
      if (item.modifier) {
        return `${baseClass} nav__link--${item.modifier}`;
      }
      return baseClass;
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  background-color: $color-white;
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 8px 20px;

  @include respond-above(xl) {
    position: relative;
    padding: 24px 40px;
  }

  &__middle {
    display: none;

    @include respond-above(xl) {
      display: flex;
      align-items: center;
      flex: 1 0 auto;
    }
  }

  &__left,
  &__right {
    flex: 1 0 auto;

    @include respond-above(xl) {
      flex: 0 1 auto;
    }
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__right {
    display: flex;
    justify-content: flex-end;
    text-align: right;

    @include respond-above(xl) {
      -webkit-justify-content: flex-end;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    font-size: 24px;
    line-height: 1;
    padding: 8px;

    &:last-child {
      margin-right: -7px;
    }

    @include respond-above(xl) {
      padding: 10px;
    }

    @include respond-above(sm) {
      padding: 12px;
    }
  }

  :deep(.button--icon--has-text .icon) {
    @include respond-below(xl) {
      margin-right: 0;
    }
  }

  &__logo {
    vertical-align: middle;
    width: 141px;

    @include respond-above(xl) {
      width: 205px;
    }
  }

  &__text {
    @include t__utility-label-20;
  }
}

.template-index .header {
  transition: 0.25s ease background;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  * {
    transition: 0.2s;
  }

  *,
  :deep(button),
  :deep(.icon-button__notification) {
    color: $color-white !important;
  }

  .header__left {
    display: block;
    filter: invert(1);

    @include respond-above(xl) {
      display: none;
    }
  }

  &--mega-nav-open {
    background: $color-white;

    *,
    :deep(button) {
      color: $color-black !important;
    }
  }
}

.nav {
  display: flex;
  gap: 24px;

  .button {
    &.nav__link--highlight {
      color: $color-red !important;
    }
  }

  &__link {
    @include utility-label(14px);
    text-decoration: none;
  }
}

.slide-in-right-enter-active,
.slide-in-right-leave-active {
  transition: transform 0.25s ease-out, opacity 0.25s ease-out;
  opacity: 1;
}

.slide-in-right-enter-from,
.slide-in-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>

<style lang="scss">
.fixed-header {
  padding-top: 86px;

  @include respond-above(md) {
    padding-top: 96px;
  }

  @include respond-above(xl) {
    padding-top: 128px;
  }

  &.template-index {
    padding-top: 34px;

    @include respond-above(xl) {
      padding-top: 36px;
    }
  }

  .header-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 4;
  }

  .header__icon.icon-button--has-text .icon {
    @include respond-below(md) {
      margin-right: 0 !important;
    }
  }
}
</style>
